import React, { useState,useRef } from "react";
import {
  Home,
  TrendingUp,
  Users,
  BarChart3,
  Package,
  DollarSign,
  Target,
} from "lucide-react";

import GmvMetricsSection from "./GmvMetricsSection";
import OrderAnalyticsSection from "./OrderAnalyticsSection";
import LeadsTable from "./leadsTable";
import SalesRepPerformancePanel from "./SalesRepPerformancePanel";
import LeadsByProjectTypeChart from "./LeadsByProjectTypeChart";
import EcommerceMetrics from "./infocards";





/* ---------------- SIMPLE UI COMPONENTS (NO EXTRA FILES) ---------------- */

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-1 py-1 md:px-3 md:py-2 rounded-md text-xs md:text-sm font-medium border  transition ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Badge = ({ children }) => (
  <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-600 text-white">
    {children}
  </span>
);

const Card = ({ children, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-lg shadow p-4 hover:shadow-md cursor-pointer"
  >
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`mb-2 flex items-center justify-between ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-sm font-medium">{children}</h3>
);

const CardContent = ({ children }) => <div>{children}</div>;

/* ---------------- MAIN COMPONENT ---------------- */

function MDC() {
  const [activeView, setActiveView] = useState("dashboard");
  const [activeTab, setActiveTab] = useState("leads");


  const leadsRef = useRef(null);
const rfqRef = useRef(null);
const salesRepRef = useRef(null);
const productRef = useRef(null);
const brandRef = useRef(null);
const marketRef = useRef(null);

const scrollAndHighlight = (ref) => {
  if (ref.current) {
    ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    ref.current.classList.add("highlight");
    setTimeout(() => ref.current?.classList.remove("highlight"), 1200);
  }
};

const handleInfoCardClick = (type) => {
  switch (type) {
    case "leads":
      scrollAndHighlight(leadsRef);
      break;
    case "rfq":
      scrollAndHighlight(rfqRef);
      break;
    case "salesrep":
      scrollAndHighlight(salesRepRef);
      break;
    case "product":
      scrollAndHighlight(productRef);
      break;
    case "brand":
      scrollAndHighlight(brandRef);
      break;
    case "market":
      scrollAndHighlight(marketRef);
      break;
    default:
      break;
  }
};


  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
     <header className="bg-white border-b sticky top-0 z-50">
  <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
    <div className="flex items-center gap-4 md:gap-16">

      <nav className="flex flex-row md:flex-row gap-2 md:gap-16">
        {[
          { key: "dashboard", label: "Overview", icon: <Home className="h-4 w-4" /> },
          { key: "gmv", label: "GMV Analytics", icon: <TrendingUp className="h-4 w-4" /> },
          { key: "sales", label: "Sales & Leads", icon: <Users className="h-4 w-4" /> },
          { key: "orders", label: "Order Analytics", icon: <BarChart3 className="h-4 w-4" /> },
        ].map((item) => (
          <Button
            key={item.key}
            onClick={() => setActiveView(item.key)}
            className={`
              flex items-center gap-1
              px-1 py-1
              md:px-3 md:py-2
              text-sm md:text-base
              ${
                activeView === item.key
                  ? "bg-blue-800 text-white"
                  : ""
              }
            `}
          >
            {item.icon}
            <span>{item.label}</span>
          </Button>
        ))}
      </nav>

    </div>
  </div>
</header>


      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6">
        {activeView === "dashboard" && (
          <>
            <h2 className="text-lg  md:text-3xl font-bold mb-1 md:mb-2">
              Brand Partner Overview
            </h2>
            <p className="text-gray-600 mb-4 md:mb-6">
              Track sales, leads and revenue
            </p>

            {/* METRICS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-6 mb-8">
              <Card onClick={() => setActiveView("gmv")}>
                <CardHeader>
                  <CardTitle>Total GMV</CardTitle>
                  <DollarSign className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg md:text-2xl font-bold">$550,000</div>
                   <p className="text-xs text-green-600 mt-1">↑ 12% from last month</p>
                </CardContent>
              </Card>

              <Card onClick={() => setActiveView("sales")}>
                <CardHeader>
                  <CardTitle>Active Leads</CardTitle>
                  <Users className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg md:text-2xl font-bold">47</div>
                  <p className="text-xs text-green-600 mt-1">↑ 8 new this week</p>
                </CardContent>
              </Card>

              <Card onClick={() => setActiveView("orders")}>
                <CardHeader>
                  <CardTitle>Total Orders</CardTitle>
                  <Package className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg  md:text-2xl font-bold">305</div>
                   <p className="text-xs text-green-600 mt-1">↑ 18% from last month</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Conversion Rate</CardTitle>
                  <Target className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg md:text-2xl font-bold">24%</div>
                   <p className="text-xs text-green-600 mt-1">↑ 3% from last month</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>
                   Top Region by GMV
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-md md:text-xl font-bold">East Region</div>
                  <p className="text-sm text-gray-600 mt-1">$520,000 GMV</p>
                </CardContent>
              </Card>

               <Card>
                <CardHeader>
                  <CardTitle>
                    Top Performing Rep
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-md md:text-xl font-bold">Priya Sharma</div>
                  <p className="text-sm text-gray-600 mt-1">28% conversion rate</p>
                </CardContent>
              </Card>

               <Card>
                <CardHeader>
                  <CardTitle>
                    Avg. Response Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                <div className="text-md md:text-xl font-bold">2.1 hours</div>
                  <p className="text-xs text-green-600 mt-1">↑ 0.3 hrs faster</p>
                </CardContent>
              </Card>
            </div>

{/* TABS CONTAINER */}
<div className="space-y-2 md:space-y-6">
  {/* TAB HEADER */}
  <div className="inline-flex rounded-full bg-gray-100 p-1 gap-1 md:gap-2">
    {[
      { key: "leads", label: "Leads & RFQ" },
      { key: "gmv", label: "GMV Overview" },
      { key: "sales", label: "Sales Performance" },
      { key: "orders", label: "Order Analytics" },
    ].map((tab) => (
      <button
        key={tab.key}
        onClick={() => setActiveTab(tab.key)}
        className={`
          px-3 py-1 md:px-6 md:py-2
          text-xs md:text-sm
          font-medium
          rounded-full
          transition-all
          ${
            activeTab === tab.key
              ? "bg-white text-black shadow"
              : "text-gray-600 hover:text-black"
          }
        `}
      >
        {tab.label}
      </button>
    ))}
  </div>

  {/* TAB CONTENT */}
  {activeTab === "leads" && (
    <div className="space-y-4 md:space-y-6">
     <EcommerceMetrics onCardClick={handleInfoCardClick} />
      <LeadsByProjectTypeChart />
    </div>
  )}

  {activeTab === "gmv" && (
    <div className="space-y-4 md:space-y-6">
      <GmvMetricsSection />
    </div>
  )}

  {activeTab === "sales" && (
    <div className="space-y-4 md:space-y-6">
      <LeadsByProjectTypeChart />
      <SalesRepPerformancePanel />
    </div>
  )}

  {activeTab === "orders" && (
    <div className="space-y-4 md:space-y-6">
      <OrderAnalyticsSection />
    </div>
  )}
</div>



          </>
        )}

      {/* GMV VIEW */}
{activeView === "gmv" && (
  <div className="space-y-4 md:space-y-6">
    <div>
      <Button
        onClick={() => setActiveView("dashboard")}
        className="bg-white"
      >
        ← Back to Overview
      </Button>
    </div>
    <GmvMetricsSection />
  </div>
)}

{/* SALES & LEADS VIEW */}
{activeView === "sales" && (
  <div className="space-y-4 md:space-y-6">
    <div>
      <Button
        onClick={() => setActiveView("dashboard")}
        className="bg-white "
      >
        ← Back to Overview
      </Button>
    </div>
       <EcommerceMetrics onCardClick={handleInfoCardClick} />
    <LeadsByProjectTypeChart/>
    <SalesRepPerformancePanel />
    <LeadsTable />
    
  </div>
)}

{/* ORDER ANALYTICS VIEW */}
{activeView === "orders" && (
  <div className="space-y-4 md:space-y-6">
    <div>
      <Button
        onClick={() => setActiveView("dashboard")}
        className="bg-white  "
      >
        ← Back to Overview
      </Button>
    </div>
    <OrderAnalyticsSection />
  </div>
)}

      </main>
    </div>
  );
}

export default MDC;
